import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MoreHorizontal, Edit, Trash2, X } from 'lucide-react';
import FormatTools from '../FormatTools/FormatTools';
import { Menu } from '@headlessui/react';
import { toast } from 'react-hot-toast';

const ConceptList = ({ concepts = [], onEdit, onDelete, onMarkAsHard, nudgeState, onDismissNudge }) => {
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [activeMenu, setActiveMenu] = useState(null);
  const menuRefs = useRef({});
  const editInputRef = useRef(null);
  const [bulletPointMode, setBulletPointMode] = useState(false);
  const [numberedListMode, setNumberedListMode] = useState(false);
  const [listCounter, setListCounter] = useState(1);

  const handleSaveEdit = useCallback(async () => {
    if (editingId) {
      try {
        await onEdit(editingId, editText);
        setEditingId(null);
        setEditText('');
        setBulletPointMode(false);
        setNumberedListMode(false);
        toast.success('Concept updated successfully');
      } catch (error) {
        // Show error toast and keep the edit mode open
        toast.error('Failed to save changes. Please try again.');
        console.error('Error saving concept:', error);
      }
    }
  }, [editingId, editText, onEdit]);

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
    setBulletPointMode(false);
    setNumberedListMode(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editingId && editInputRef.current && !editInputRef.current.contains(event.target)) {
        if (!event.target.closest('.format-button')) {
          handleSaveEdit();
        }
      }
      if (activeMenu && menuRefs.current[activeMenu] && !menuRefs.current[activeMenu].contains(event.target)) {
        setActiveMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingId, handleSaveEdit, activeMenu]);

  const handleStartEdit = (concept) => {
    setEditingId(concept.id);
    setEditText(concept.text);
    setBulletPointMode(false);
    setNumberedListMode(false);
    
    setTimeout(() => {
      if (editInputRef.current) {
        editInputRef.current.focus();
        editInputRef.current.setSelectionRange(concept.text.length, concept.text.length);
      }
    }, 0);
  };

  const renderFormattedText = (text) => {
    // Split text into paragraphs (double line breaks)
    const paragraphs = text.split('\n\n');
    
    return paragraphs.map((paragraph, pIndex) => {
      // Split paragraph into lines
      const lines = paragraph.split('\n');
      
      return (
        <div key={pIndex} className={`${pIndex > 0 ? 'mt-4' : ''}`}>
          {lines.map((line, index) => {
            if (line.startsWith('• ')) {
              return <div key={index} className="ml-4">• {line.substring(2)}</div>;
            } else if (line.match(/^\d+\. /)) {
              return <div key={index} className="ml-4">{line}</div>;
            } else if (line.startsWith('    ')) {
              return <div key={index} className="ml-8">{line.trim()}</div>;
            } else {
              return <div key={index}>{line}</div>;
            }
          })}
        </div>
      );
    });
  };

  const formatSelection = (type, e) => {
    e?.preventDefault();
    const textarea = editInputRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    
    if (start === end) {
      const cursorPosition = textarea.selectionStart;
      const textBeforeCursor = text.substring(0, cursorPosition);
      const textAfterCursor = text.substring(cursorPosition);
      
      let formattedText;
      switch (type) {
        case 'bullet':
          formattedText = `${textBeforeCursor}• ${textAfterCursor}`;
          break;
        case 'number':
          formattedText = `${textBeforeCursor}1. ${textAfterCursor}`;
          break;
        case 'indent':
          formattedText = `${textBeforeCursor}    ${textAfterCursor}`;
          break;
        default:
          formattedText = text;
      }
      
      setEditText(formattedText);
      
      setTimeout(() => {
        const newPosition = cursorPosition + (type === 'bullet' ? 2 : type === 'number' ? 3 : 4);
        textarea.focus();
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
      return;
    }

    // Get selected text and split into lines
    const selectedText = text.substring(start, end);
    const lines = selectedText.split('\n');
    
    // Format each line based on type
    const formattedLines = lines.map((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return '';
      
      switch (type) {
        case 'bullet':
          return line.startsWith('• ') ? line : `• ${trimmedLine}`;
        case 'number':
          return line.match(/^\d+\. /) ? line : `${index + 1}. ${trimmedLine}`;
        case 'indent':
          return line.startsWith('    ') ? line : `    ${trimmedLine}`;
        default:
          return line;
      }
    });

    // Combine the formatted lines
    const formattedText = formattedLines.join('\n');
    
    // Replace the selected text with formatted text
    const newText = text.substring(0, start) + formattedText + text.substring(end);
    setEditText(newText);

    // Maintain focus and update selection
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start,
        start + formattedText.length
      );
    }, 0);
  };

  const addBulletPoint = () => {
    setNumberedListMode(false);
    const newMode = !bulletPointMode;
    setBulletPointMode(newMode);
    if (newMode) {
      formatSelection('bullet');
    }
  };

  const addNumberedList = () => {
    setBulletPointMode(false);
    const newMode = !numberedListMode;
    setNumberedListMode(newMode);
    setListCounter(1);
    if (newMode) {
      formatSelection('number');
    }
  };

  const addIndentation = () => formatSelection('indent');
  const addParagraph = () => formatSelection('paragraph');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (bulletPointMode || numberedListMode)) {
      e.preventDefault();
      const textarea = editInputRef.current;
      const cursorPosition = textarea.selectionStart;
      const text = textarea.value;
      const textBeforeCursor = text.substring(0, cursorPosition);
      const textAfterCursor = text.substring(cursorPosition);
      
      // Check if the current line is empty (just a bullet/number)
      const currentLine = textBeforeCursor.split('\n').pop();
      if (currentLine.trim() === '•' || currentLine.match(/^\d+\.$/)) {
        // If empty, remove the formatting and exit bullet/number mode
        const newText = textBeforeCursor.substring(0, textBeforeCursor.length - currentLine.length) + '\n' + textAfterCursor;
        setEditText(newText);
        setBulletPointMode(false);
        setNumberedListMode(false);
      } else {
        // Add new bullet point or number
        const newLine = bulletPointMode ? '\n• ' : `\n${listCounter + 1}. `;
        const newText = textBeforeCursor + newLine + textAfterCursor;
        setEditText(newText);
        if (numberedListMode) {
          setListCounter(prev => prev + 1);
        }
        
        // Set cursor position after the new bullet/number
        setTimeout(() => {
          const newPosition = cursorPosition + newLine.length;
          textarea.focus();
          textarea.setSelectionRange(newPosition, newPosition);
        }, 0);
      }
    }
  };

  const handleDelete = (conceptId) => {
    if (activeMenu === conceptId) {
      setActiveMenu(null);
    }
    onDelete(conceptId);
  };

  const handleFormatStateChange = (type) => {
    if (type === 'bullet') {
      setNumberedListMode(false);
      setBulletPointMode(prev => !prev);
    } else if (type === 'number') {
      setBulletPointMode(false);
      setNumberedListMode(prev => !prev);
      setListCounter(1);
    }
  };

  useEffect(() => {
    // Auto-resize textarea when editing starts
    if (editingId && editInputRef.current) {
      const textarea = editInputRef.current;
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight to fit content
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [editingId, editText]);

  const handleTextareaChange = (e) => {
    setEditText(e.target.value);
    // Auto-resize as user types
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  const ConceptMenu = ({ conceptId }) => {
    return (
      <Menu as="div" className="relative inline-block text-left">
        {({ open }) => (
          <>
            <Menu.Button className="menu-button text-neutral-900 hover:text-cranberry-700 transition-colors duration-200">
              <MoreHorizontal size={18} />
            </Menu.Button>
            
            {open && (
              <Menu.Items static className="absolute right-0 mt-2 w-48 origin-top-right rounded-xl bg-neutral-50 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 border-2 border-neutral-900">
                <div className="px-1 py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`${
                          active ? 'bg-neutral-200 text-neutral-900' : 'text-neutral-900'
                        } group flex w-full items-center rounded-lg px-2 py-2 text-sm font-medium`}
                        onClick={() => handleStartEdit(concepts.find(c => c.id === conceptId))}
                      >
                        <Edit className="mr-2 h-5 w-5" aria-hidden="true" />
                        Edit
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`${
                          active ? 'bg-red-600 text-white' : 'text-neutral-900'
                        } group flex w-full items-center rounded-lg px-2 py-2 text-sm font-medium`}
                        onClick={() => handleDelete(conceptId)}
                      >
                        <Trash2 className="mr-2 h-5 w-5" aria-hidden="true" />
                        Delete
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            )}
          </>
        )}
      </Menu>
    );
  };

  const hasHardConcept = concepts.some(concept => concept.is_highlighted);

  return (
    <div className="space-y-3">
      {!hasHardConcept && (
        <p className="text-xs text-neutral-500 italic leading-relaxed">
          Tap &quot;mark as hard&quot; on the one you&apos;d blank on under pressure - if several feel tricky, start with the worst. You&apos;ll work through the rest one by one as you master each.
        </p>
      )}

      {nudgeState === 'visible' && (
        <div className="border-l-2 border-cranberry-500 bg-cranberry-50 rounded-r-lg px-4 py-3 flex items-start gap-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-cranberry-800 leading-snug mb-1">
              Which part would you blank on under pressure?
            </p>
            <p className="text-xs text-cranberry-700 leading-relaxed">
              Not the most interesting - the one that costs you marks. Tap that one. If several feel tricky, start with the worst. You&apos;ll work through the rest one by one as you master each.
            </p>
          </div>
          <button
            onClick={onDismissNudge}
            className="text-cranberry-500 hover:text-cranberry-700 mt-0.5 flex-shrink-0"
            aria-label="Dismiss mark as hard tip"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <ul className="space-y-2">
        {concepts && concepts.map((concept) => (
        <li
          key={concept.id}
          className={`flex items-start justify-between p-2 rounded-md transition-colors duration-200 
            ${concept.is_highlighted ? 'bg-amber-50 border border-amber-200' : ''}
            hover:bg-blue-50 relative pl-8`}
        >
          <span className="absolute left-2 top-[0.5rem] text-gray-600 dark:text-gray-400 text-lg leading-none">
            •
          </span>
          {editingId === concept.id ? (
            <div className="flex-grow relative">
              <textarea
                ref={editInputRef}
                value={editText}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                className="w-full p-2 pr-24 border border-gray-300 rounded resize-none overflow-hidden"
                style={{ minHeight: '100px' }}
              />
              <FormatTools
                onBulletPoint={addBulletPoint}
                onNumberedList={addNumberedList}
                onIndentation={addIndentation}
                onParagraph={addParagraph}
                bulletPointActive={bulletPointMode}
                numberedListActive={numberedListMode}
                conceptIndex={concept.id}
                onFormatSelection={formatSelection}
                onFormatStateChange={handleFormatStateChange}
              />
              <div className="flex justify-end space-x-2 mt-2">
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <>
              <div
                className={`text-neutral-900 flex-grow cursor-pointer ${
                  concept.is_highlighted ? 'font-medium' : ''
                }`}
                onClick={() => handleStartEdit(concept)}
              >
                {renderFormattedText(concept.text)}
              </div>
              <div className="flex items-start space-x-2 ml-2">
                <button
                  onClick={() => onMarkAsHard(concept.id)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors whitespace-nowrap ${
                    concept.is_highlighted
                      ? 'bg-amber-200 text-amber-800 border-amber-300'
                      : 'border-neutral-200 text-neutral-500 hover:border-amber-300 hover:text-amber-700'
                  }`}
                  aria-label={concept.is_highlighted ? 'Unmark as hard' : 'Mark as hard'}
                >
                  {concept.is_highlighted ? 'hard' : 'mark as hard'}
                </button>
                <ConceptMenu conceptId={concept.id} />
              </div>
            </>
          )}
        </li>
        ))}
      </ul>
    </div>
  );
};

export default ConceptList;
